import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../services/api';
import LinkifyText from './LinkifyText';
import './ProjectNotes.css';

const ProjectNotes = ({ projectId, onUpdate }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formContent, setFormContent] = useState('');

  useEffect(() => {
    if (projectId) {
      loadNotes();
    }
  }, [projectId]);

  const loadNotes = async () => {
    if (!projectId) {
      setNotes([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await projectsAPI.getNotes(projectId);
      setNotes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading notes:', error);
      console.error('Error details:', error.response?.data);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = () => {
    setEditingNote(null);
    setFormContent('');
    setShowForm(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setFormContent(note.content);
    setShowForm(false); // Don't show the separate form, we'll edit inline
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formContent.trim()) {
      alert('Please enter note content');
      return;
    }

    try {
      if (editingNote) {
        await projectsAPI.updateNote(editingNote.id, { content: formContent.trim() });
        setEditingNote(null); // Clear editing state
        setFormContent('');
        setShowForm(false);
      } else {
        await projectsAPI.createNote(projectId, { content: formContent.trim() });
        setShowForm(false);
        setFormContent('');
      }

      loadNotes();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggle = async (noteId) => {
    try {
      await projectsAPI.toggleNote(noteId);
      loadNotes();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error toggling note:', error);
      alert('Failed to toggle note');
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await projectsAPI.deleteNote(noteId);
      loadNotes();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingNote(null);
    setFormContent('');
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setFormContent('');
    setShowForm(false);
  };

  if (loading) {
    return <div className="project-notes-loading">Loading notes...</div>;
  }

  const checkedCount = notes.filter(n => n.is_checked).length;
  const totalCount = notes.length;

  return (
    <div className="project-notes">
      <div className="notes-header">
        <div>
          <h3>Notes / Ideas / Checklist</h3>
          {totalCount > 0 && (
            <p className="notes-stats">
              {checkedCount} of {totalCount} items checked
            </p>
          )}
        </div>
        <button className="btn-add" onClick={handleCreateNote}>
          + Add Note
        </button>
      </div>

      {showForm && !editingNote && (
        <form onSubmit={handleSubmit} className="note-form">
          <h4>Add New Note</h4>
          <textarea
            value={formContent}
            onChange={(e) => setFormContent(e.target.value)}
            placeholder="Enter your note, idea, or checklist item..."
            rows="3"
            required
            autoFocus
          />
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              Add
            </button>
          </div>
        </form>
      )}

      {notes.length > 0 && (
        <div className="notes-checklist">
          {notes.map((note) => {
            // Check if this note is being edited
            const isEditing = editingNote && editingNote.id === note.id;
            
            if (isEditing) {
              return (
                <form key={note.id} onSubmit={handleSubmit} className="note-form-inline">
                  <textarea
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    placeholder="Enter your note, idea, or checklist item..."
                    rows="2"
                    required
                    autoFocus
                    className="note-edit-input"
                  />
                  <div className="note-edit-actions">
                    <button type="submit" className="btn-save-small">
                      Save
                    </button>
                    <button type="button" className="btn-cancel-small" onClick={handleCancelEdit}>
                      Cancel
                    </button>
                  </div>
                </form>
              );
            }
            
            return (
              <div key={note.id} className={`note-item ${note.is_checked ? 'checked' : ''}`}>
                <label className="note-checkbox-label">
                  <input
                    type="checkbox"
                    checked={note.is_checked || false}
                    onChange={() => handleToggle(note.id)}
                    className="note-checkbox"
                  />
                  <span className="note-content">
                    <LinkifyText text={note.content} />
                  </span>
                </label>
                <div className="note-actions">
                  <button
                    className="btn-edit-small"
                    onClick={() => handleEditNote(note)}
                    title="Edit"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button
                    className="btn-delete-small"
                    onClick={() => handleDelete(note.id)}
                    title="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {notes.length === 0 && !showForm && (
        <div className="notes-empty">
          <p>No notes yet.</p>
          <p>Click "+ Add Note" to start adding notes, ideas, or checklist items to this project.</p>
        </div>
      )}
    </div>
  );
};

export default ProjectNotes;
